// Build-time channel configuration.
//
// The SDK ships as one source tree built into three channels — `staging`,
// `production`, and `extension`. They differ only by values baked in at build
// time by Vite's `define` (see vite.config.js), never by forking the source.
// `buildConfig` is a pure function so the branches are unit-testable; `config`
// is the frozen singleton the rest of the SDK reads.

export const ENVIRONMENTS = ['staging', 'production', 'extension'];

const DEFAULT_ENV = 'production';

/**
 * Normalize raw build-time values into the frozen channel config. Unknown or
 * missing `env` falls back to production so a mis-built bundle stays safe.
 *
 * `buildSha`/`builtAt` are build-identity metadata, not the human-bumped
 * `version` - they change on every single build (a git short SHA + an ISO
 * timestamp), which is what makes them useful for answering "is the CDN
 * actually serving what I just deployed?" without having to `curl`+grep the
 * live bundle. `unknown`/`null` fallbacks keep this safe when a build runs
 * outside a git checkout.
 */
export function buildConfig({ env, apiBase, version, buildSha, builtAt } = {}) {
  const resolved = ENVIRONMENTS.includes(env) ? env : DEFAULT_ENV;
  return Object.freeze({
    env: resolved,
    apiBase: typeof apiBase === 'string' ? apiBase : '',
    version: typeof version === 'string' ? version : '0.0.0',
    buildSha: typeof buildSha === 'string' ? buildSha : 'unknown',
    builtAt: typeof builtAt === 'string' ? builtAt : null,
    isProduction: resolved === 'production',
    isStaging: resolved === 'staging',
    isExtension: resolved === 'extension',
  });
}

// `typeof` guards keep this safe under Vitest/jsdom, where the define'd globals
// are absent and the config falls back to production defaults.
/* global __PLAYBID_ENV__, __PLAYBID_API_BASE__, __PLAYBID_VERSION__, __PLAYBID_BUILD_SHA__, __PLAYBID_BUILT_AT__ */
export const config = buildConfig({
  env: typeof __PLAYBID_ENV__ === 'string' ? __PLAYBID_ENV__ : undefined,
  apiBase: typeof __PLAYBID_API_BASE__ === 'string' ? __PLAYBID_API_BASE__ : undefined,
  version: typeof __PLAYBID_VERSION__ === 'string' ? __PLAYBID_VERSION__ : undefined,
  buildSha: typeof __PLAYBID_BUILD_SHA__ === 'string' ? __PLAYBID_BUILD_SHA__ : undefined,
  builtAt: typeof __PLAYBID_BUILT_AT__ === 'string' ? __PLAYBID_BUILT_AT__ : undefined,
});
